'use client';

import { Briefcase, Coins, Globe, Users } from 'lucide-react';
import { motion } from 'motion/react';
import React, { useState } from 'react';
import { Cell, Pie, PieChart, Tooltip } from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { ContentType } from 'recharts/types/component/Tooltip';

const CustomTooltip: ContentType<ValueType, NameType> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const percentage = payload[0].value as number;
    return (
      <div className="hidden sm:flex rounded-md border border-[rgb(34,50,83)] bg-gray-900 px-3 py-1">
        <p className="text-white">{`${payload[0].name}: ${Number(percentage)}%`}</p>
      </div>
    );
  }

  return null;
};

const COLORS = ['#60A5FA', '#34D399', '#F472B6', '#A78BFA'];

const tokenomicsData = [
  { name: 'Circulating', value: 88, icon: Coins },
  { name: 'Team', value: 4, icon: Users },
  { name: 'Treasury', value: 4, icon: Briefcase },
  { name: 'Community', value: 4, icon: Globe },
];

const escrowAddresses = [
  'FTmD2gHE9QF4MP23fJZjbxLbGynCpnG1NWGPCyuAdjZX',
  'CXpWQfXogyswJ3aWavmKBU3t1wbo7Az33GNaPmszUirM',
  'nZJL4F9jY9VtXnyyAam5pdofrXCtURkHeR9VpgmWAPT',
  'AxYuRGthyPjJheySbeRQSETP4YmYjAHSV4hhbq4Xvkpj',
  '5kd1JiAZGJ2pzrCEk517i5FNuFPbrTN6KdAxVxrcAn7ptm5AXN1D7JnRRLD9ii4s9afGC3JQxH68MjXhSNh8aqTC',
  '5BCKsmPS8GNoR6rWt3uAN2EH6rTgGkH3qRt6fzDjzdYu',
  'DTSNEPsSwL8EMjeZeduiDhKctzgQ8b7jm8SQyzjjnEvP',
  '3MLsqNzc68es5BaeieYNQvyExA5VtSZFf3TZt6GqXquy',
  '5LmxgF6Yw2VRtr7uzG6cuRbKoBQve3AEQgV1BJBSHbaa',
];

export default function Index() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [clicked, setClicked] = useState<boolean>(false);

  const onPieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(null);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold text-center mb-8 p-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
          Casting spells to protect your bags
        </h1>
        <div className="border-t border-gray-700 py-4">
          <div className="flex flex-col md:flex-row gap-8 sm:gap-12">
            <motion.div
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              className="bg-transparent border border-[rgb(34,50,83)] rounded-2xl p-4 sm:p-8 h-fit"
            >
              <h2 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">Tokenomics</h2>
              <h4 className="text-base sm:text-sm font-semibold text-gray-500 mb-4 sm:mb-4">
                Here is how we split up our coin. Strategic allocations ensure sustainable project
                growth and community engagement.
              </h4>

              <div className="h-[300px] w-full flex items-center justify-center mb-6 sm:mb-8">
                <PieChart width={300} height={300}>
                  <Pie
                    data={tokenomicsData}
                    cx="50%"
                    cy="50%"
                    innerRadius="45%"
                    outerRadius="80%"
                    paddingAngle={4}
                    dataKey="value"
                    onMouseEnter={onPieEnter}
                    onMouseLeave={onPieLeave}
                  >
                    {tokenomicsData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        className="transition-all"
                        offset={activeIndex === null || activeIndex === index ? 1 : '1.05'}
                        fill={COLORS[index]}
                        stroke={undefined}
                        scale={activeIndex === null || activeIndex === index ? 1 : '1.05'}
                        opacity={activeIndex === null || activeIndex === index ? 1 : 0.6}
                      />
                    ))}
                  </Pie>
                  {/* @ts-ignore */}
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {tokenomicsData.map((item, index) => (
                  <motion.div
                    key={item.name}
                    className="flex items-center gap-3 p-3 sm:p-4 rounded-lg cursor-pointer transition-all"
                    style={
                      {
                        scale: activeIndex === index ? 1.03 : undefined,
                        backgroundColor:
                          activeIndex === index ? 'var(--hover-color)' : `${COLORS[index]}20`,
                        '--hover-color': `${COLORS[index]}50`,
                      } as React.CSSProperties
                    }
                    whileHover={{ backgroundColor: 'var(--hover-color)', scale: 1.03 }}
                    data-active={`${activeIndex === index}`}
                    initial={{
                      opacity: 0,
                      scale: 0.97,
                    }}
                    animate={{
                      opacity: 1,
                      scale: 1,
                      transition: {
                        delay: index * 0.05,
                      },
                    }}
                    onMouseEnter={() => {
                      setClicked(false);
                      onPieEnter(undefined, index);
                    }}
                    onClick={() => {
                      setClicked(true);
                      onPieEnter(undefined, index);
                    }}
                    onMouseLeave={() => {
                      if (clicked) return;
                      onPieLeave();
                    }}
                  >
                    <item.icon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: COLORS[index] }} />
                    <div>
                      <div className="font-semibold" style={{ color: COLORS[index] }}>
                        {item.name}
                      </div>
                      <div className="text-sm text-gray-300">{item.value}%</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
                transition: {
                  delay: 0.05,
                },
              }}
              className="bg-transparent border border-[rgb(34,50,83)] rounded-2xl p-4 sm:p-8 h-fit"
            >
              <h2 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">Team Escrows</h2>
              <h4 className="text-base sm:text-sm font-semibold text-gray-500 mb-4 sm:mb-4">
                Time-locked smart contracts securing team tokens with gradual release schedules.
                Ensuring long-term commitment and project stability.
              </h4>
              <div className="space-y-3 sm:space-y-4">
                {escrowAddresses.map((address, index) => (
                  <div
                    key={index}
                    className="bg-gray-700/50 border border-gray-700/50 hover:border-purple-400 p-3 sm:p-4 rounded-lg transition-all hover:bg-gray-700"
                  >
                    <div className="font-semibold text-purple-400 text-xs sm:text-sm mb-1">
                      Account {index + 1}
                    </div>
                    <div className="font-mono text-xs sm:text-sm break-all">{address}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
